import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { Spiner } from "../../components/spinner/Spiner";
import Button from "../../components/button/Button";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { PointHistorySearch } from "./PointHistorySearch";
import { PointHistoryExportModal } from "./PointHistoryExportModal";
import { auth } from "../../firebase";
import { PointHistoryData } from "../../model/PointData";
import { convertDateToString, convertDateTimeToString } from "../../utility/ConvertUtily";
import { Company } from "../../model/Companies";
import companiesQuery from "../../firestore/Companies.query";
import { Progress } from "../../components/progress/Progress";
import { checkOption } from "../../controller/OptionController";
import { requestGetPointHistory, requestGetPointName } from "../../api/Point.api";
import { Groups } from "../../model/Groups";

export const PointHistoryList = () => {
  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const navigation = useNavigate();
  const dispatch = useDispatch();

  const [isLoad, setIsLoad] = useState(true);
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectNumber, setSelectNumber] = useState(50);
  const [pointHistoryList, setPointHistoryList] = useState<PointHistoryData[]>([]);
  const [searchPointHistoryList, setSearchPointHistoryList] = useState<PointHistoryData[]>([]);
  const [searchPointHistoryListFlg, setSearchPointHistoryListFlg] = useState(false);
  const [ptNameList, setPtNameList] = useState<any[]>([]);
  const [company, setCompany] = useState<Company>();
  const [firstPageFlg, setFirstPageFlg] = useState(false);
  const [nowPage, setNowPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchCondition, setSearchCondition] = useState<any>({});
  const [groupList, setGroupList] = useState<Groups[]>([]);

  useEffect(() => {
    const f = async () => {
      try {
        // 団体を取得
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
        setCompany(company);
        const result = await requestGetPointName();
        if (result.success) {
          setPtNameList(result.pointNameList);
        } else {
          console.error("ポイント名取得失敗:", result.error);
        }
        // 団体グループ取得
        const companyGroups = await companiesQuery.getGroups(company.id);
        // グループ選択モーダル用リストを作成
        const filteredGroups = companyGroups.filter((group) => group.deleteFlg === false)
        setGroupList(filteredGroups);
      } catch (error) {
        console.error(error);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      }
      setIsLoad(false);
      setFirstPageFlg(true)
    };
    f();
  },[currentUser?.tenantId]);

  // ポイント履歴一覧を取得する共通処理
  useEffect(() => {
    const fetchPointsByPage = async () => {
      const result = await requestGetPointHistory(
        nowPage,
        selectNumber,
        searchCondition
      );
      if (result.success) {
        setPointHistoryList(result.pointHistory);
        setTotalCount(result.totalCount);
      } else {
        console.error("ポイント履歴取得失敗:", result.error);
      }
      setIsLoad(false)
    };
    fetchPointsByPage();
  }, [nowPage, selectNumber, searchCondition]);

  // 検索ボタンが押されたときの処理
  const handleSearch = (condition: any) => {
    setIsLoad(true);
    setSearchCondition(condition);
    setNowPage(1); // 検索時は常に1ページ目に戻す
  };

  // ページング処理
  const pagingFunc = useCallback((page: number) => {
    setSearchPointHistoryListFlg(false);
    setFirstPageFlg(false);
    setNowPage(page);
  }, []);

  // 検索時と表示件数変更時に1ページ目を表示する
  useEffect(() => {
    setFirstPageFlg(true)
  }, [searchPointHistoryListFlg, searchPointHistoryList, selectNumber])

  const selectNumberList = [
    { id: 50, name: "50" },
    { id: 100, name: "100" },
    { id: 200, name: "200" },
  ];

  const columns: GridCol[] = [
    {
      field: "userId",
      headerName: "利用者番号",
      style: "w-1/21",
      sort: false
    },
    {
      field: "userName",
      headerName: "名前",
      style: "w-2/21",
      sort: false
    },
    {
      field: "ptCategory",
      headerName: "カテゴリ",
      style: "w-4/21",
      sort: false
    },
    {
      field: "ptType",
      headerName: "タイプ",
      style: "w-4/21",
      sort: false
    },
    {
      field: "name",
      headerName: "ポイント名",
      style: "w-4/21",
      sort: false
    },
    {
      field: "expirationDate",
      headerName: "有効期限",
      style: "w-1/21",
      sort: false
    },
    {
      field: "occurrenceDate",
      headerName: "ポイント発生日",
      style: "w-1/21",
      sort: false
    },
    {
      field: "tranType",
      headerName: "取引種別",
      style: "w-1/21",
      sort: false
    },
    {
      field: "point",
      headerName: "ポイント数",
      style: "w-1/21",
      sort: false
    },
    {
      field: "heldPoints",
      headerName: "保有ポイント数",
      style: "w-1/21",
      sort: false
    },
    {
      field: "info",
      headerName: "",
      style: "w-1/21",
      sort: false
    },
  ];

  const rowList: GridRow[] = (searchPointHistoryListFlg? searchPointHistoryList : pointHistoryList).map<GridRow>((item) => {
    const expirationDate = item.expirationDate as any;
    const occurrenceDate = item.occurrenceDate as any;
    return{
      rowData: {
        userId: item.displayUserId,
        userName: item.userName,
        tranType: item.tranType,
        ptCategory: item.ptCategoryName,
        ptType: item.ptTypeName,
        name: item.ptName,
        expirationDate: expirationDate ? convertDateToString(new Date(expirationDate)) : "" ,
        occurrenceDate: occurrenceDate ? convertDateTimeToString(new Date(occurrenceDate)) : "",
        point: (<div className={"text-right"}>{item.grantPoints}</div>),
        heldPoints: (<div className={"text-right"}>{item.heldPoints}</div>),
        info: (<Button className={"sm:px-4 whitespace-nowrap"} onClick={()=> navigation(`point/${item.id}/info`)}>詳細</Button>),
        occurrenceDateTime: occurrenceDate,
        createDateTime: item.createAt
      },
      style: 'sm:whitespace-normal'
    };
  });
  
  return (
    <div className="h-full">
      <div className="flex items-center px-6 pt-6 pb-4">
        <div className="flex-auto">
          <h2 className="text-xl font-semibold text-gray-900">
            ポイント履歴一覧
          </h2>
        </div>
        <Progress
          subText={ConstMessage.PROGRESS_SUB_TEXT}
          open={loadProgress >= 0 && loadProgress < 100}
          progressNum={loadProgress}
          color={"green"}
        />
        <div>
          <Button
            size={"sm"}
            onClick={async () => {
              if (await checkOption("point", dispatch)) {
                setIsDialogOpen(!isDialogOpen);
                }
              }
            }
            children={"履歴出力"}
          />
        </div>
      </div>
      <div>
        {isDialogOpen && (
          <PointHistoryExportModal
            children
            open={isDialogOpen}
            toggle={() => {setIsDialogOpen(!isDialogOpen)}}
            closeOnClickOutside={false}
            setLoadProgress={setLoadProgress}
            company={company}
            pointHistoryList={searchPointHistoryListFlg? searchPointHistoryList : pointHistoryList}
            dispName={"pointHistory"}
            searchCondition={searchCondition}
          />
        )}
          <>
            <div className={"px-6  pb-3"}>
              <PointHistorySearch
                pointHistoryList={pointHistoryList}
                setSearchPointHistoryList={setSearchPointHistoryList}
                setSearchPointHistoryFlg={setSearchPointHistoryListFlg}
                groupList={groupList}
                ptNameList={ptNameList}
                company={company}
                onSearch={handleSearch}
                searchCondition={searchCondition}
              />
            </div>
            {isLoad === true ? <Spiner /> : (
              <div className={"px-6 pb-6"}>
                <TableList
                  header
                  gridCol={columns}
                  gridRow={rowList}
                  gridRowLength={totalCount}
                  setSelectNumber={setSelectNumber}
                  selectNumberList={selectNumberList}
                  limit={selectNumber}
                  minWidth="min-w-[900px]"
                  defaultSortKey={columns[7].field}
                  sortKeyList={[{key: "occurrenceDateTime", sort: "desc"}, {key: "userId", sort: "asc"}, {key: "createDateTime", sort: "desc"}]}
                  firstPageFlg={firstPageFlg}
                  setFirstPageFlg={setFirstPageFlg}
                  pagingFunc={pagingFunc}
                />
              </div>
            )}
          </>
      </div>
    </div>
  );
}